import React, { useState } from 'react';
import { X, ChevronDown, ChevronUp, Download, CheckCircle2, AlertCircle } from 'lucide-react';
import { useGamingStore } from '../../store/gamingStore';

function formatBytes(value?: number): string {
  if (!value || value <= 0) return '0 B';
  if (value >= 1024 * 1024 * 1024) return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${Math.floor(value)} B`;
}

const GameDownloadsOverlay: React.FC = () => {
  const { gameDownloads, dismissGameDownload } = useGamingStore();
  const [expanded, setExpanded] = useState(false);

  if (gameDownloads.length === 0) return null;

  const activeDownloads = gameDownloads.filter(d => d.status === 'downloading' || d.status === 'verifying');
  const completedCount = gameDownloads.filter(d => d.status === 'completed').length;
  const errorCount = gameDownloads.filter(d => d.status === 'error').length;
  const allDone = activeDownloads.length === 0;

  // Cumulative progress
  const totalBytes = gameDownloads.reduce((sum, d) => sum + (d.totalBytes || 0), 0);
  const downloadedBytes = gameDownloads.reduce((sum, d) => sum + (d.bytesDownloaded || 0), 0);
  const totalSpeed = activeDownloads.reduce((sum, d) => sum + (d.bytesPerSecond || 0), 0);
  const cumulativeProgress = totalBytes > 0
    ? Math.floor((downloadedBytes / totalBytes) * 100)
    : Math.floor(gameDownloads.reduce((sum, d) => sum + (d.progress || 0), 0) / gameDownloads.length);

  // Summary label
  const summaryLabel = allDone
    ? (errorCount > 0 ? `${completedCount} terminé${completedCount > 1 ? 's' : ''}, ${errorCount} erreur${errorCount > 1 ? 's' : ''}` : `${completedCount} téléchargement${completedCount > 1 ? 's' : ''} terminé${completedCount > 1 ? 's' : ''}`)
    : `${activeDownloads.length} téléchargement${activeDownloads.length > 1 ? 's' : ''} en cours`;

  const canDismissAll = allDone;

  const dismissAll = () => {
    gameDownloads.forEach(d => {
      if (d.status === 'completed' || d.status === 'error') dismissGameDownload(d.id);
    });
  };

  return (
    <div className="fixed bottom-0 left-16 right-0 z-[80]">
      <div className="bg-[#1a1a1a]/95 backdrop-blur-xl border-t border-white/[0.06] shadow-2xl shadow-black/50 overflow-hidden">
        {/* ── Summary bar ── */}
        <div className="px-4 py-3 flex items-center gap-3">
          {/* Icon */}
          <div className="shrink-0">
            {allDone ? (
              errorCount > 0 ? <AlertCircle className="h-4.5 w-4.5 text-red-400" /> : <CheckCircle2 className="h-4.5 w-4.5 text-emerald-400" />
            ) : (
              <Download className="h-4.5 w-4.5 text-white/80 animate-pulse" />
            )}
          </div>

          {/* Text + progress */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-white/90 truncate">{summaryLabel}</span>
              {!allDone && totalSpeed > 0 && (
                <span className="text-[10px] text-white/40 shrink-0 ml-2">{formatBytes(totalSpeed)}/s</span>
              )}
            </div>
            <div className="h-[3px] rounded-full bg-white/10 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${allDone ? (errorCount > 0 ? 'bg-red-400' : 'bg-emerald-400') : 'bg-white'}`}
                style={{ width: `${Math.max(0, Math.min(100, cumulativeProgress))}%` }}
              />
            </div>
            {!allDone && (
              <div className="flex items-center justify-between mt-1">
                <span className="text-[10px] text-white/35">
                  {formatBytes(downloadedBytes)}{totalBytes > 0 ? ` / ${formatBytes(totalBytes)}` : ''}
                </span>
                <span className="text-[10px] text-white/35">{cumulativeProgress}%</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0">
            {gameDownloads.length > 1 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="h-6 w-6 inline-flex items-center justify-center rounded-md text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                aria-label="Détails"
              >
                {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
              </button>
            )}
            {canDismissAll && (
              <button
                onClick={dismissAll}
                className="h-6 w-6 inline-flex items-center justify-center rounded-md text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                aria-label="Fermer"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ── Single download (always expanded) or detail list ── */}
        {(gameDownloads.length === 1 || expanded) && (
          <div className="border-t border-white/[0.06] px-3 py-2 space-y-1.5 max-h-48 overflow-y-auto gaming-scrollbar">
            {gameDownloads.map((dl) => {
              const progress = Math.max(0, Math.min(100, Math.floor(dl.progress || 0)));
              const canDismiss = dl.status === 'completed' || dl.status === 'error';
              const isActive = dl.status === 'downloading' || dl.status === 'verifying';

              return (
                <div key={dl.id} className="flex items-center gap-2.5 py-1.5 group">
                  {/* Status dot */}
                  <div className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                    dl.status === 'completed' ? 'bg-emerald-400' :
                    dl.status === 'error' ? 'bg-red-400' :
                    dl.status === 'verifying' ? 'bg-amber-400 animate-pulse' :
                    'bg-white/60 animate-pulse'
                  }`} />

                  {/* Name + progress */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-white/70 truncate font-medium">{dl.gameSlug}</span>
                      <span className="text-[10px] text-white/30 shrink-0 ml-2">
                        {dl.status === 'completed' ? 'Terminé' :
                         dl.status === 'error' ? 'Erreur' :
                         dl.status === 'verifying' ? 'Vérification' :
                         `${progress}%`}
                      </span>
                    </div>
                    {isActive && (
                      <div className="h-[2px] rounded-full bg-white/[0.08] overflow-hidden mt-1">
                        <div
                          className="h-full rounded-full bg-white/50 transition-all duration-200"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                    {dl.error && dl.status === 'error' && (
                      <p className="text-[9px] text-red-300/70 mt-0.5 truncate">{dl.error}</p>
                    )}
                  </div>

                  {/* Dismiss */}
                  {canDismiss && (
                    <button
                      onClick={() => dismissGameDownload(dl.id)}
                      className="h-5 w-5 inline-flex items-center justify-center rounded text-white/0 group-hover:text-white/40 hover:!text-white/70 transition-colors"
                      aria-label="Fermer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Detail toggle hint for multiple downloads ── */}
        {gameDownloads.length > 1 && !expanded && (
          <button
            onClick={() => setExpanded(true)}
            className="w-full border-t border-white/[0.06] px-4 py-1.5 text-[10px] text-white/30 hover:text-white/50 hover:bg-white/[0.03] transition-colors text-center"
          >
            Afficher les détails
          </button>
        )}
      </div>
    </div>
  );
};

export default GameDownloadsOverlay;
